import styled from "styled-components";
import { COLORS } from "@bikdotai/bik-component-library";
import { BodySecondary } from "../../../TypographyStyle";

/** Flex container for consistent spacing */
export const Flex = styled.div<{ gap?: string; align?: string; justify?: string; column?: boolean; width?: string }>`
  display: flex;
  flex-direction: ${(props) => (props.column ? "column" : "row")};
  gap: ${(props) => props.gap || "0px"};
  align-items: ${(props) => props.align || "stretch"};
  justify-content: ${(props) => props.justify || "flex-start"};
  width: ${(props) => props.width || "auto"};
`;

/** Generic relative container */
export const RelativeContainer = styled.div`
  position: relative;
`;
 export const Divider = styled.div`
  height: 1px;
  border-radius: 2px;
  background: #D9D9D9;
`

/** Main wrapper for the entire component */
export const TicketActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
`;

/** Dot for priority levels */
export const PriorityDotStyled = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
  margin-right: 8px;
  flex-shrink: 0;
`;

/** Main container for AssignTicketSelector */
export const SelectorContainer = styled.div`
  position: relative;
  width: 100%;
`;

/** Trigger button for AssignTicketSelector */
export const SelectorTrigger = styled.div`
  border: 1px solid ${COLORS?.stroke?.primary || "#E0E0E0"};
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  cursor: pointer;
  background: #fff;
  justify-content: space-between;
  min-height: 44px;
`;

/** Popup menu for AssignTicketSelector */
export const SelectorPopup = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 360px;
  background: #fff;
  border: 1px solid ${COLORS?.stroke?.primary || "#E0E0E0"};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  padding: 8px;
`;

/** Tabs header container */
export const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${COLORS?.stroke?.primary || "#E0E0E0"};
  margin-bottom: 8px;
`;

/** Individual tab item */
export const TabItem = styled.div<{ active: boolean }>`
  flex: 1;
  text-align: center;
  padding: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  border-bottom: ${(props) => (props.active ? "2px solid #731DCF" : "none")};
  color: ${(props) => (props.active ? "#731DCF" : "#616161")};
`;

/** Search input in popup */
export const SearchInputStyled = styled.input`
  width: 100%;
  padding: 8px 12px;
  margin-bottom: 8px;
  border: 1px solid ${COLORS?.stroke?.primary || "#E0E0E0"};
  border-radius: 4px;
  font-size: 14px;
  outline: none;
`;

/** Row for team/staff lists */
export const ItemRow = styled.div<{ selected: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 6px;
  background: ${(props) => (props.selected ? "#E8F5E9" : "transparent")};

  &:hover {
    background: ${(props) => (props.selected ? "#E8F5E9" : "#F5F5F5")};
  }
`;

/** Wrapper for each sub-action card */
export const ActionCard = styled.div<{ error?: boolean; isDragging?: boolean }>`
  border: 1px solid
    ${(props) =>
        props.error
            ? COLORS?.content?.negative || "#FF4444"
            : COLORS?.stroke?.primary || "#E8E8E8"};
  border-radius: 8px;
  margin-bottom: 20px;
  padding: 12px;
  background: #ffffff;
  box-shadow: ${(props) =>
        props.isDragging ? "0 8px 24px rgba(0,0,0,0.12)" : "none"};
  transition: box-shadow 0.15s ease;
`;

/** Header area of action card */
export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

/** Error message container below action card content */
export const ErrorContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  margin-top: 8px;
`;

/** Button for adding labels / generic action button */
export const GhostButton = styled.div<{ isPopulated?: boolean }>`
  border: 1px solid #731DCF;
  color: #731DCF;
  padding: ${(props) => (props.isPopulated ? "4px 12px" : "12px")};
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  width: ${(props) => (props.isPopulated ? "fit-content" : "100%")};
  height: ${(props) => (props.isPopulated ? "32px" : "44px")};
  background: #ffffff;
`;

/** Flex container for label tags */
export const LabelsFlex = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

/** Dotted "Add an action" button */
export const AddActionButton = styled.div`
  border: 1px dashed ${COLORS?.stroke?.primary || "#D0D0D0"};
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9e9e9e;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
`;

/** Dropdown menu for adding new actions */
export const ActionMenu = styled.div`
  border: 1px solid ${COLORS?.stroke?.primary || "#E8E8E8"};
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  margin-top: 8px;
  padding: 8px 0;
  position: absolute;
  width: 100%;
  z-index: 10;
  top: 100%;
`;

/** Single item in the action menu */
export const MenuItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;

  &:hover {
    background-color: #f8f8f8;
  }
`;

/** Title for menu items */
export const MenuItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS?.content?.primary || "#333"};
`;

/** Caption/description for menu items */
export const MenuItemCaption = styled.div`
  font-size: 12px;
  color: ${COLORS?.content?.secondary || "#757575"};
  margin-top: 2px;
`;

/** Vertical connector line between action cards */
export const ConnectorLine = styled.div`
  position: absolute;
  left: 50%;
  bottom: -18px;
  width: 1px;
  height: 16px;
  background-color: ${COLORS?.stroke?.primary || "#E8E8E8"};
`;

/** Drag handle for action cards */
export const DragHandle = styled(Flex)`
  cursor: grab;
`;

/** Delete button for action cards */
export const DeleteButton = styled(Flex)`
  cursor: pointer;
`;

/** Scrollable list for popups */
export const ScrollableList = styled.div`
  max-height: 240px;
  overflow-y: auto;
`;

/** Container for 'no results' message */
export const NoResultsContainer = styled.div`
  padding: 16px;
  text-align: center;
  color: #9e9e9e;
  font-size: 14px;
`;

/** Title for the actions section */
export const ActionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS?.content?.primary || "#212121"};
  margin-bottom: 16px;
`;

/** Title for individual action cards */
export const CardTitle = styled(BodySecondary)`
  font-weight: 600 !important;
  color: ${COLORS?.content?.secondary};
  flex: 1;
  text-align: center;
`;

/** Name/Label in team/staff list rows */
export const ItemRowName = styled(BodySecondary) <{ selected: boolean }>`
  font-weight: ${(props) => (props.selected ? 600 : 400)} !important;
  color: ${(props) =>
        props.selected
            ? COLORS?.content?.positive || "#731DCF"
            : COLORS?.content?.primary || "#212121"} !important;
`;


